import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, KeyRound, Pencil, Plane, UserPlus } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function Executives() {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "executive" });
  const [editing, setEditing] = useState(null);
  const [leaveFor, setLeaveFor] = useState(null);
  const [leaveDates, setLeaveDates] = useState({ from: "", to: "" });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => api.get("/users").then((r) => r.data) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["users"] });

  const create = useMutation({
    mutationFn: () => api.post("/users", form).then((r) => r.data),
    onSuccess: (u) => {
      toast.success(`${u.role === "admin" ? "Admin" : "Executive"} ${u.name} created`);
      setForm({ name: "", email: "", password: "", role: "executive" });
      invalidate();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const update = useMutation({
    mutationFn: ({ id, patch }) => api.patch(`/users/${id}`, patch).then((r) => r.data),
    onSuccess: (u) => {
      toast.success(u?.released ? `${u.name} marked on leave · ${u.released} lead${u.released === 1 ? "" : "s"} moved back to the pool` : u?.leave_from && !u?.on_leave ? `Leave scheduled ${u.leave_from} → ${u.leave_to}` : "Account updated");
      setEditing(null);
      setLeaveFor(null);
      invalidate();
      ["tickets", "stats", "team", "admin-overview"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const resetPassword = (u) => {
    const pw = window.prompt(`One-time password for ${u.name} (min 6 characters). They will be asked to set their own password at next login:`);
    if (pw && pw.length >= 6) update.mutate({ id: u.id, patch: { password: pw } });
    else if (pw) toast.error("Password must be at least 6 characters");
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
      <div className="overflow-x-auto rounded-xl border-2 border-black bg-white">
        <table className="w-full text-left text-sm" data-testid="users-table">
          <thead className="bg-black text-[11px] uppercase tracking-wider text-white">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="hidden px-4 py-3 sm:table-cell">Email</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Open / Resolved</th>
              <th className="px-4 py-3">Active</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200">
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.email}`}>
                {editing?.id === u.id ? (
                  <td className="px-4 py-3" colSpan={2}>
                    <div className="flex flex-wrap gap-2">
                      <input className="field-input !py-1.5 !text-sm" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Full name" data-testid={`user-edit-name-${u.email}`} />
                      <input className="field-input !py-1.5 !text-sm" type="email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} placeholder="Email" data-testid={`user-edit-email-${u.email}`} />
                    </div>
                  </td>
                ) : (
                  <>
                    <td className="px-4 py-3 font-semibold">
                      {u.name}
                      <span className="block text-xs text-zinc-500 sm:hidden">{u.email}</span>
                    </td>
                    <td className="hidden px-4 py-3 sm:table-cell">{u.email}</td>
                  </>
                )}
                <td className="px-4 py-3"><span className={`pill ${u.is_master ? "bg-[#FACC15] border-black" : u.role === "admin" ? "bg-black text-[#FACC15] border-black" : "bg-[#FEF9C3] border-[#EAB308]"}`} data-testid={`user-role-${u.email}`}>{u.is_master ? "master admin" : u.role}</span>
                  {u.on_leave && <span className="pill ml-1 border-[#7C3AED] bg-[#F5F3FF] text-[#6D28D9]" data-testid={`user-on-leave-badge-${u.email}`}>On leave{u.leave_to ? ` · till ${u.leave_to}` : ""}</span>}
                  {!u.on_leave && u.leave_from && <span className="pill ml-1 border-[#7C3AED] bg-white text-[#6D28D9]" data-testid={`user-leave-scheduled-${u.email}`}>Leave {u.leave_from} → {u.leave_to}</span>}
                </td>
                <td className="px-4 py-3 font-mono">{u.open_tickets} / {u.resolved_tickets}</td>
                <td className="px-4 py-3">
                  <Switch
                    checked={u.active}
                    disabled={u.id === me.id || u.is_master || (u.role === "admin" && !me.is_master)}
                    onCheckedChange={(v) => update.mutate({ id: u.id, patch: { active: v } })}
                    className="data-[state=checked]:bg-black"
                    data-testid={`user-active-switch-${u.email}`}
                  />
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex flex-wrap justify-end gap-1.5">
                    {editing?.id === u.id ? (
                      <>
                        <button className="btn-dark !px-3 !py-1.5 !text-xs" onClick={() => update.mutate({ id: u.id, patch: { name: editing.name, email: editing.email } })} disabled={update.isPending} data-testid={`user-edit-save-${u.email}`}>
                          <Check className="h-3.5 w-3.5" /> Save
                        </button>
                        <button className="btn-ghost !px-3 !py-1.5 !text-xs" onClick={() => setEditing(null)} data-testid={`user-edit-cancel-${u.email}`}>Cancel</button>
                      </>
                    ) : u.role === "admin" && !me.is_master && u.id !== me.id ? (
                      <span className="text-xs text-zinc-500" data-testid={`user-locked-${u.email}`}>Managed by master admin</span>
                    ) : (
                      <>
                        {me.is_master && u.id !== me.id && (
                          <select className="rounded-lg border-2 border-black bg-white px-2 py-1 text-xs font-bold" value={u.is_master ? "master_admin" : u.role} onChange={(e) => update.mutate({ id: u.id, patch: { role: e.target.value } })} data-testid={`user-role-select-${u.email}`}>
                            <option value="executive">executive</option>
                            <option value="admin">admin</option>
                            <option value="master_admin">master admin</option>
                          </select>
                        )}
                        <button className="btn-ghost !px-3 !py-1.5 !text-xs" onClick={() => setEditing({ id: u.id, name: u.name, email: u.email })} data-testid={`user-edit-${u.email}`}>
                          <Pencil className="h-3.5 w-3.5" /> Edit
                        </button>
                        <button className="btn-ghost !px-3 !py-1.5 !text-xs" onClick={() => resetPassword(u)} data-testid={`user-reset-password-${u.email}`}>
                          <KeyRound className="h-3.5 w-3.5" /> Reset password
                        </button>
                        {u.on_leave ? (
                          <button className="btn-dark !px-3 !py-1.5 !text-xs" onClick={() => update.mutate({ id: u.id, patch: { on_leave: false, leave_from: "", leave_to: "" } })} disabled={update.isPending} data-testid={`user-back-from-leave-${u.email}`}>
                            <Plane className="h-3.5 w-3.5" /> Back from leave
                          </button>
                        ) : (
                          <button className="btn-ghost !border-[#7C3AED] !px-3 !py-1.5 !text-xs !text-[#6D28D9] hover:!bg-[#F5F3FF]" onClick={() => { setLeaveFor(u); setLeaveDates({ from: u.leave_from || "", to: u.leave_to || "" }); }} disabled={!u.active} data-testid={`user-on-leave-${u.email}`}>
                            <Plane className="h-3.5 w-3.5" /> {u.leave_from ? "Edit leave" : "Leave"}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
        className="brut-card brut-shadow h-fit space-y-4 p-5"
        data-testid="create-executive-form"
      >
        <h3 className="flex items-center gap-2 font-display text-xl font-bold"><UserPlus className="h-5 w-5" /> Add team member</h3>
        <p className="-mt-2 text-sm text-zinc-600">Create a login for a teammate with a <strong>one-time password</strong>. On first login they must set their own password, and can then edit their profile, picture and password themselves (or use "Forgot password" on the login page). You can reset a password or <strong>Deactivate</strong> (block login) anytime.</p>
        <div>
          <label className="field-label">Role</label>
          <div className={`grid gap-2 ${me.is_master ? "grid-cols-3" : "grid-cols-1"}`} data-testid="new-role-toggle">
            {(me.is_master ? ["executive", "admin", "master_admin"] : ["executive"]).map((r) => (
              <button
                type="button"
                key={r}
                onClick={() => setForm({ ...form, role: r })}
                className={`rounded-xl border-2 border-black px-3 py-2.5 text-sm font-bold capitalize transition-colors ${form.role === r ? "bg-black text-white" : "bg-white hover:bg-[#FEF9C3]"}`}
                data-testid={`new-role-${r}`}
              >
                {r.replace("_", " ")}
              </button>
            ))}
          </div>
          {!me.is_master && <p className="mt-1 text-xs text-zinc-500">Only the master admin can add admins.</p>}
        </div>
        <div>
          <label className="field-label">Full name</label>
          <input className="field-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required minLength={2} data-testid="new-exec-name-input" />
        </div>
        <div>
          <label className="field-label">Email</label>
          <input className="field-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="new-exec-email-input" />
        </div>
        <div>
          <label className="field-label">One-time password</label>
          <input className="field-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} data-testid="new-exec-password-input" />
        </div>
        <button type="submit" className="btn-primary w-full" disabled={create.isPending} data-testid="create-executive-button">
          {create.isPending ? "Creating…" : `Create ${form.role} account`}
        </button>
      </form>
      <AlertDialog open={Boolean(leaveFor)} onOpenChange={(o) => !o && setLeaveFor(null)}>
        <AlertDialogContent className="border-2 border-black" data-testid="leave-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Leave for {leaveFor?.name}</AlertDialogTitle>
            <AlertDialogDescription>
              Mark them on leave <strong>now</strong> — their unresolved leads ({leaveFor?.open_tickets ?? 0}) move back to the unassigned pool immediately — or <strong>schedule</strong> dates below: they go on leave automatically on the start date (leads released then) and come back the day after the end date.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="field-label">Leave from</label>
              <input type="date" className="field-input !py-2 !text-sm" value={leaveDates.from} onChange={(e) => setLeaveDates({ ...leaveDates, from: e.target.value })} data-testid="leave-from-date" />
            </div>
            <div>
              <label className="field-label">Leave to</label>
              <input type="date" className="field-input !py-2 !text-sm" value={leaveDates.to} onChange={(e) => setLeaveDates({ ...leaveDates, to: e.target.value })} data-testid="leave-to-date" />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="leave-cancel-button">Cancel</AlertDialogCancel>
            {leaveFor?.leave_from && (
              <button type="button" className="btn-ghost !py-2 !text-sm" onClick={() => update.mutate({ id: leaveFor.id, patch: { leave_from: "", leave_to: "" } })} data-testid="leave-clear-button">Clear schedule</button>
            )}
            {leaveDates.from || leaveDates.to ? (
              <AlertDialogAction className="bg-[#6D28D9] hover:bg-[#5B21B6]" onClick={() => update.mutate({ id: leaveFor.id, patch: { leave_from: leaveDates.from || leaveDates.to, leave_to: leaveDates.to || leaveDates.from } })} data-testid="leave-schedule-button">
                Schedule leave
              </AlertDialogAction>
            ) : (
              <AlertDialogAction className="bg-[#6D28D9] hover:bg-[#5B21B6]" onClick={() => update.mutate({ id: leaveFor.id, patch: { on_leave: true } })} data-testid="leave-confirm-button">
                Mark on leave now & release leads
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
